import cn, { Argument as ClassName } from 'classnames'
import React, { memo, useState } from 'react'
import { useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import { Button } from 'primereact/button'
import { Column } from 'primereact/column'

import { Pool, useCompanyGetPoolsQuery, usePoolDeletePoolMutation } from '../../../common/redux/api/web-api'
import { notifyError, notifySuccess } from '../../../common/features/notificationsManager/Notify'
import { PageLayoutContainer } from '../../../lib/page-layout-container'
import { WidgetLayout } from '../../../lib/widget-layout'
import { RootState } from '../../redux/store'
import useText from '../../../common/hooks/useText'
import { confirm } from '../../../common/uikit/ConfirmDialog'
import { Table } from '../../../common/uikit/Table'

import { CreatePoolDialog } from './dialogs/create-pool-dialog'

export type PoolsPageProps = {
    className?: ClassName
    style?: React.CSSProperties
}

export const PoolsPage = memo(function PoolsPage({ className, style }: PoolsPageProps): React.ReactElement {
    const text = useText()
    const user = useSelector((state: RootState) => state.user)
    const [openAddModal, setOpenAddModal] = useState(false)

    const cancel = () => setOpenAddModal(false)

    const {
        currentData: pools,
        isLoading,
        refetch,
    } = useCompanyGetPoolsQuery({ companyId: user?.selectedCompanyId }, { skip: !user.selectedCompanyId })

    const [removePool] = usePoolDeletePoolMutation()

    const save = () => {
        refetch()
        setOpenAddModal(false)
    }

    const removePools = async (poolId?: string) => {
        if (!poolId) return

        const confirmed = await confirm({
            header: text('lblProceedConfirmation'),
            message: text('msgRemovePool'),
            variant: 'danger',
        })
        if (!confirmed) return

        try {
            await removePool({ poolId, companyId: user.selectedCompanyId }).unwrap()
            notifySuccess(text('msgPoolRemoved'))
            refetch()
        } catch (e) {
            console.error(e)
            notifyError(text('msgPoolNotRemoved'))
        }
    }

    return (
        <PageLayoutContainer className={cn(className)} style={style}>
            <WidgetLayout
                headerButtons={
                    <Button
                        label={text('btnAdd')}
                        icon="pi pi-plus"
                        type="button"
                        data-testid="add-icon"
                        onClick={() => setOpenAddModal(true)}
                    />
                }
            >
                <Table
                    value={pools ?? []}
                    loading={isLoading}
                    emptyMessage={text('msgNoPoolsFound')}
                    className="mt-3"
                    data-testid="pools-table"
                >
                    <Column
                        field="name"
                        header={text('lblName')}
                        body={(pool: Pool) => <Link to={`/pool/${pool.id}`}>{pool.name}</Link>}
                    />
                    <Column
                        body={(pool: Pool) => (
                            <div className="flex justify-content-end gap-2">
                                <Button
                                    type="button"
                                    icon="pi pi-times"
                                    size="small"
                                    outlined
                                    rounded
                                    severity="danger"
                                    data-testid={`remove-pool-${pool.id}`}
                                    aria-label={text('btnRemove')}
                                    tooltip={text('btnRemove')}
                                    tooltipOptions={{ position: 'top' }}
                                    onClick={() => removePools(pool.id)}
                                />
                            </div>
                        )}
                    />
                </Table>
                <CreatePoolDialog visible={openAddModal} onHide={cancel} onSuccess={save} />
            </WidgetLayout>
        </PageLayoutContainer>
    )
})
